import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";
import { AuctionMedia, ListingInfo } from "../components/auction/AuctionMedia";
import { AuctionTimerCard } from "../components/auction/AuctionTimerCard";
import { BidPanel } from "../components/auction/BidPanel";
import { BidHistory } from "../components/auction/BidHistory";
import AuctionPaymentPanel from "../components/auction/AuctionPaymentPanel";

const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return { text: "ENDED", urgent: true };

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (days > 0) return { text: `${days}d ${hours}h ${mins}m`, urgent: false };
  if (hours > 0) return { text: `${hours}h ${mins}m ${secs}s`, urgent: hours < 1 };
  if (mins > 0) return { text: `${mins}m ${secs}s`, urgent: true };
  return { text: `${secs}s`, urgent: true };
};

const AuctionDetail = ({ user, login }) => {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [listing, setListing] = useState(null);
  const [lotListings, setLotListings] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [minNextBid, setMinNextBid] = useState(0);
  const [increments, setIncrements] = useState([50, 100, 250]);
  const timerRef = useRef(null);
  const [watching, setWatching] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(null);

  const fetchAuction = useCallback(async () => {
    try {
      const response = await api.get(`/auctions/${auctionId}`);
      setAuction(response.data.auction);
      setListing(response.data.listing);
      setLotListings(response.data.lot_listings || []);
      setBids(response.data.bids || []);
      setTimeRemaining(response.data.time_remaining_seconds || 0);
      setMinNextBid(response.data.min_next_bid || 0);
      setIncrements(response.data.suggested_increments || [50, 100, 250]);
      setBidAmount(response.data.min_next_bid?.toString() || "");
    } catch (error) {
      logger.error("Failed to fetch auction:", error);
      toast.error("Auction not found");
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  const fetchWatchStatus = useCallback(async () => {
    try {
      const res = await api.get(`/auctions/${auctionId}/watching`);
      setWatching(res.data.watching);
    } catch (err) {
      logger.error(err);
    }
  }, [auctionId]);

  const fetchPaymentStatus = useCallback(async () => {
    try {
      const res = await api.get("/bidder/payment-status");
      setHasPaymentMethod(res.data.has_payment_method);
    } catch (err) {
      logger.error(err);
      setHasPaymentMethod(false);
    }
  }, []);

  useEffect(() => {
    fetchAuction();
    if (user) {
      fetchWatchStatus();
      fetchPaymentStatus();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [auctionId, user, fetchAuction, fetchWatchStatus, fetchPaymentStatus]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [auction]);

  const toggleWatch = async () => {
    if (!user) {
      login(`/auctions/${auctionId}`);
      return;
    }
    setWatchLoading(true);
    try {
      if (watching) {
        await api.delete(`/auctions/${auctionId}/watch`);
        setWatching(false);
        toast.success("Stopped watching auction");
      } else {
        await api.post(`/auctions/${auctionId}/watch`);
        setWatching(true);
        toast.success("Watching auction — you'll be notified of updates");
      }
    } catch (err) {
      toast.error("Failed to update watch status");
    } finally {
      setWatchLoading(false);
    }
  };

  const handleBid = async () => {
    if (!user) {
      toast.error("Please sign in to bid");
      login(`/auctions/${auctionId}`);
      return;
    }

    if (hasPaymentMethod !== true) {
      toast.error("Add a payment method before bidding.");
      navigate(`/dashboard?return_to=${encodeURIComponent(`/auctions/${auctionId}`)}`);
      return;
    }

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount < minNextBid) {
      toast.error(`Bid must be at least $${minNextBid.toLocaleString()}`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/auctions/${auctionId}/bid`, { amount });
      toast.success("Bid placed successfully!");
      fetchAuction();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bid");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      toast.error("Please sign in to purchase");
      login(`/auctions/${auctionId}`);
      return;
    }

    if (hasPaymentMethod !== true) {
      toast.error("Add a payment method before using Buy Now.");
      navigate(`/dashboard?return_to=${encodeURIComponent(`/auctions/${auctionId}`)}`);
      return;
    }

    const fee = auction.buy_now_price * 0.03;
    const total = auction.buy_now_price + fee;
    if (!window.confirm(
      `Confirm Buy Now: ${auction.buy_now_price.toLocaleString()} + ${fee.toFixed(2)} processing fee = ${total.toFixed(2)} total?`
    )) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/auctions/${auctionId}/buy-now`);
      toast.success(response.data.message);
      fetchAuction();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to purchase");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickBid = (increment) => {
    const currentBid = auction?.current_bid || auction?.starting_bid || 0;
    setBidAmount((currentBid + increment).toString());
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!auction || !listing) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#A1A1AA]">Auction not found</p>
          <Link to="/auctions" className="text-[#D4AF37] hover:underline mt-2 block">
            Browse Auctions
          </Link>
        </div>
      </div>
    );
  }

  const isUserWinning = Boolean(user && auction.viewer_is_winning);
  const isUserSeller = Boolean(user && auction.viewer_is_seller);
  const timerDisplay = formatTimeRemaining(timeRemaining);

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-sm text-[#71717A]">
            <Link to="/auctions" className="hover:text-[#D4AF37]">
              Auctions
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#A1A1AA]">{listing.title}</span>
          </div>
          <button
            onClick={toggleWatch}
            disabled={watchLoading}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              watching
                ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30"
                : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
            }`}
            data-testid="watch-auction-btn"
          >
            {watching ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {watching ? "Watching" : "Watch"}
          </button>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <AuctionMedia auction={auction} listing={listing} lotListings={lotListings} />
            <ListingInfo listing={listing} />
          </div>

          <div className="lg:col-span-2">
            <AuctionTimerCard
              auction={auction}
              timerDisplay={timerDisplay}
              timeRemaining={timeRemaining}
            />
            <BidPanel
              auction={auction}
              user={user}
              isUserWinning={isUserWinning}
              isUserSeller={isUserSeller}
              hasPaymentMethod={hasPaymentMethod}
              timeRemaining={timeRemaining}
              bidAmount={bidAmount}
              setBidAmount={setBidAmount}
              minNextBid={minNextBid}
              increments={increments}
              submitting={submitting}
              onBid={handleBid}
              onBuyNow={handleBuyNow}
              onQuickBid={handleQuickBid}
              onSignIn={() => login(`/auctions/${auctionId}`)}
            />
            <BidHistory bids={bids} />
            <AuctionPaymentPanel auctionId={auctionId} user={user} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionDetail;
